/**
 * MapControlsContainer - Container Component for MapControls
 *
 * Container component responsible for:
 * - Zustand store connection for fog, region, and marker settings
 * - Providing an onUpdateSettings callback for toggling settings
 * - Accepting passthrough props from the parent (MapPanelContainer) for
 *   shape/drawing-related props that are managed externally
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { useStore, useStorePrimitive, useActions } from '../../hooks/useStore';
import type { StoreState } from '../../types/types';
import MapControls from './MapControls';
import type { MapControlsPassthroughProps } from './MapControls';

const MapControlsContainer: React.FC<MapControlsPassthroughProps> = (passthroughProps) => {
  // ---------------------------------------------------------------------------
  // Store Connection - Container Responsibility
  // ---------------------------------------------------------------------------
  const fogSettings = useStore(state => state.settings?.fogOfWar);
  const showMapRegions = useStorePrimitive(state => state.settings?.showMapRegions);
  const showMapMarkers = useStorePrimitive(state => state.settings?.showMapMarkers);
  const actions = useActions();

  const handleUpdateSettings = (settings: Partial<StoreState['settings']>) => {
    actions.updateSettings(settings);
  };

  // ---------------------------------------------------------------------------
  // Render: Delegate to Presenter
  // ---------------------------------------------------------------------------
  return (
    <MapControls
      {...passthroughProps}
      fogSettings={fogSettings}
      showMapRegions={showMapRegions}
      showMapMarkers={showMapMarkers}
      onUpdateSettings={handleUpdateSettings}
    />
  );
};

export default MapControlsContainer;
